import { absoluteUrl, resolveUrl, stripHtml } from "@/lib/seo";
import { organizationProfile } from "@/lib/site-data";
import { getWebsiteProductImageUrl } from "@/lib/product-presentation";

function formatSchemaDate(date) {
    return date.toISOString().slice(0, 10);
}

function toSchemaDate(value) {
    if (!value) {
        return undefined;
    }

    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return undefined;
    }

    return formatSchemaDate(parsed);
}

function buildPriceValidUntil(now = new Date()) {
    return formatSchemaDate(
        new Date(Date.UTC(now.getUTCFullYear() + 1, now.getUTCMonth(), now.getUTCDate()))
    );
}

function getOfferPrice(option) {
    const discountedPrice = Number(option?.discounted_price);
    if (Number.isFinite(discountedPrice) && discountedPrice > 0) {
        return discountedPrice;
    }

    const regularPrice = Number(option?.price);
    if (Number.isFinite(regularPrice) && regularPrice > 0) {
        return regularPrice;
    }

    return null;
}

export function buildOrganizationSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: organizationProfile.name,
        legalName: organizationProfile.legalName,
        url: absoluteUrl("/"),
        logo: absoluteUrl(organizationProfile.logoPath),
        email: organizationProfile.email,
        telephone: organizationProfile.phone,
        sameAs: organizationProfile.sameAs,
        address: {
            "@type": "PostalAddress",
            ...organizationProfile.address,
        },
    };
}

export function buildWebsiteSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: organizationProfile.name,
        url: absoluteUrl("/"),
        publisher: {
            "@type": "Organization",
            name: organizationProfile.name,
        },
    };
}

export function buildBreadcrumbSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: absoluteUrl(item.path),
        })),
    };
}

export function buildCollectionPageSchema({ title, description, path, products = [] }) {
    return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: title,
        description: stripHtml(description),
        url: absoluteUrl(path),
        mainEntity: {
            "@type": "ItemList",
            numberOfItems: products.length,
            itemListElement: products.map((product, index) => ({
                "@type": "ListItem",
                position: index + 1,
                url: absoluteUrl(`/product/${product.alt_id}`),
                name: product.name,
            })),
        },
    };
}

export function buildTechArticleSchema({
    title,
    description,
    path,
    datePublished,
    dateModified,
    articleSection,
}) {
    const published = toSchemaDate(datePublished || dateModified);
    const modified = toSchemaDate(dateModified || datePublished);

    return {
        "@context": "https://schema.org",
        "@type": "TechArticle",
        headline: title,
        description: stripHtml(description),
        url: absoluteUrl(path),
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": absoluteUrl(path),
        },
        ...(published ? { datePublished: published } : {}),
        ...(modified ? { dateModified: modified } : {}),
        ...(articleSection ? { articleSection } : {}),
        author: {
            "@type": "Organization",
            name: organizationProfile.name,
            url: absoluteUrl("/"),
        },
        publisher: {
            "@type": "Organization",
            name: organizationProfile.name,
            logo: {
                "@type": "ImageObject",
                url: absoluteUrl(organizationProfile.logoPath),
            },
        },
    };
}

export function buildWebApplicationSchema({
    name,
    description,
    path,
    applicationCategory = "UtilitiesApplication",
    featureList = [],
}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name,
        description: stripHtml(description),
        url: absoluteUrl(path),
        applicationCategory,
        operatingSystem: "Any",
        browserRequirements: "Requires JavaScript. Requires HTML5.",
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
        },
        provider: {
            "@type": "Organization",
            name: organizationProfile.name,
            url: absoluteUrl("/"),
        },
        ...(featureList.length
            ? {
                  featureList,
              }
            : {}),
    };
}

export function buildProductSchema(product) {
    const descriptionFromDetails =
        product?.details?.find((detail) => detail.type === "DESCRIPTION")?.text ||
        product?.details?.find((detail) => detail.type === "SUMMARY")?.text ||
        "";

    const imageUrls = [
        ...(Array.isArray(product?.main_image) ? product.main_image.map((image) => image.media_name || image.thumbnail) : []),
        ...(Array.isArray(product?.media) ? product.media.map((image) => image.original || image.thumbnail) : []),
    ].filter(Boolean);
    const fallbackImageUrl = getWebsiteProductImageUrl(product);
    const resolvedImageUrls =
        imageUrls.length > 0 ? imageUrls : fallbackImageUrl ? [fallbackImageUrl] : [];

    const priceValidUntil = buildPriceValidUntil();

    const offers = (product?.buying_options || [])
        .map((option) => {
            const price = getOfferPrice(option);
            if (!Number.isFinite(price) || String(option.name || "").toLowerCase().includes("contact sales")) {
                return null;
            }

            return {
                "@type": "Offer",
                priceCurrency: "USD",
                price: price.toFixed(2),
                priceValidUntil,
                availability: "https://schema.org/InStock",
                url: absoluteUrl(`/product/${product.alt_id}`),
            };
        })
        .filter(Boolean);

    // Google requires Product rich results to include offers, reviews, or aggregateRating.
    // Skip Product schema on contact-sales-only pages until qualifying structured data exists.
    if (!offers.length) {
        return null;
    }

    const schema = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product?.name,
        sku: product?.alt_id,
        description: stripHtml(product?.description || descriptionFromDetails),
        url: absoluteUrl(`/product/${product?.alt_id}`),
        image: [...new Set(resolvedImageUrls.map((image) => resolveUrl(image)))],
        brand: {
            "@type": "Brand",
            name: organizationProfile.name,
        },
    };

    if (offers.length === 1) {
        schema.offers = offers[0];
    } else if (offers.length > 1) {
        schema.offers = offers;
    }

    return schema;
}
